/**
 * Shared authenticated-fetch wrapper for every builder→Worker call.
 *
 * One choke point carrying the cross-cutting concerns that were previously
 * duplicated (and drifting) across auth-api.js, template-api.js and
 * preview-api.js:
 *   - `credentials: 'include'` so the `pb_token` cookie is always sent.
 *   - Bearer-header attachment via `getAuthHeaders` (the localStorage token
 *     fallback for cross-site contexts where the cookie is stripped).
 *   - optional `timeoutMs` — an AbortController that surfaces a
 *     `TemplateApiError('Request timed out', 0, { timeout: true })`.
 *   - a single `setUnauthorizedHandler(fn)` fired on session-level 401
 *     responses. Callers whose route uses 401 for an expected validation
 *     failure (for example, an incorrect current password) opt out with
 *     `suppressUnauthorizedHandler`.
 *
 * Callers pass a fully-resolved URL (origin differs by module — template vs
 * preview) and get the raw `Response` back; each module keeps its own
 * `parseJsonResponse`/`parseBlobResponse` error mapping.
 *
 * The import of `getAuthHeaders`/`TemplateApiError` from template-api.js forms
 * an intentional module cycle: those bindings are only referenced inside
 * function bodies (call-time), never at module-eval time, so the cycle is
 * benign.
 */

import { getAuthHeaders, TemplateApiError } from '../templates/template-api.js';

let _unauthorizedHandler = null;

/**
 * Register a callback fired whenever an authed request returns HTTP 401.
 * Fires at most once per 401 response, before the Response is handed back to
 * the caller (which then maps it to its own error). Pass `null` to clear.
 *
 * @param {((info:{ url:string, status:number }) => void)|null} fn
 */
export function setUnauthorizedHandler(fn) {
  _unauthorizedHandler = typeof fn === 'function' ? fn : null;
}

/**
 * Authenticated fetch. Always sends credentials + the Bearer token; optionally
 * enforces a timeout; fires the unauthorized handler on 401 unless the caller
 * explicitly marks that status as an expected route-level response.
 *
 * A caller-supplied `signal` (user-initiated cancel) composes with `timeoutMs`
 * rather than being silently replaced by it, and its abort is rethrown
 * unwrapped so `err.name === 'AbortError'` still identifies "the user cancelled"
 * — a timeout stays a `TemplateApiError`, because those are different outcomes.
 *
 * @param {string} url  Fully-resolved request URL (origin + path).
 * @param {RequestInit & {
 *   timeoutMs?: number,
 *   suppressUnauthorizedHandler?: boolean|((response:Response) => boolean|Promise<boolean>)
 * }} [init]
 * @returns {Promise<Response>}
 */
export async function authedFetch(url, init = {}) {
  const { timeoutMs, suppressUnauthorizedHandler = false, headers, signal, ...rest } = init;

  let controller = null;
  let timer = 0;
  let onCallerAbort = null;
  if (Number.isFinite(timeoutMs) && timeoutMs > 0 && typeof AbortController !== 'undefined') {
    controller = new AbortController();
    timer = setTimeout(() => controller.abort(), timeoutMs);
    if (signal) {
      if (signal.aborted) controller.abort();
      else {
        onCallerAbort = () => controller.abort();
        signal.addEventListener('abort', onCallerAbort, { once: true });
      }
    }
  }
  const effectiveSignal = controller ? controller.signal : signal;

  let resp;
  try {
    resp = await fetch(url, {
      credentials: 'include',
      ...rest,
      ...(effectiveSignal ? { signal: effectiveSignal } : {}),
      headers: getAuthHeaders(headers || {})
    });
  } catch (err) {
    // Caller abort wins over timeout: if the user cancelled, that is the truth
    // regardless of whether the deadline also happened to pass.
    if (signal?.aborted) throw err;
    if (controller?.signal.aborted) {
      throw new TemplateApiError('Request timed out', 0, { timeout: true });
    }
    throw new TemplateApiError(`Could not reach ${url}`, 0, { cause: err?.message || String(err) });
  } finally {
    if (timer) clearTimeout(timer);
    if (onCallerAbort) signal.removeEventListener('abort', onCallerAbort);
  }

  if (resp.status === 401 && _unauthorizedHandler) {
    let suppress = suppressUnauthorizedHandler === true;
    if (typeof suppressUnauthorizedHandler === 'function') {
      try {
        suppress = Boolean(await suppressUnauthorizedHandler(resp.clone()));
      } catch {
        // If classification fails, fail safe by treating the 401 as a session
        // failure rather than potentially leaving an expired session active.
        suppress = false;
      }
    }
    if (!suppress) {
      try {
        _unauthorizedHandler({ url, status: 401 });
      } catch {
        // A misbehaving handler must not swallow the response.
      }
    }
  }
  return resp;
}
